<template>
  <article
    class="news-card"
    :class="{ featured: newsItem.featured, 'loading-state': loading }"
    @click="$emit('click', newsItem)"
  >
    <!-- Featured Badge -->
    <div v-if="newsItem.featured" class="featured-badge">
      <i class="fas fa-star"></i>
      {{ $t('news.featured') }}
    </div>

    <!-- Featured Image -->
    <div class="image-wrapper">
      <img
        v-if="featuredImage"
        :src="featuredImage"
        :alt="newsItem.title"
        class="featured-image"
        loading="lazy"
      />
      <div v-else class="image-placeholder">
        <i class="fas fa-newspaper"></i>
      </div>
    </div>

    <!-- Content -->
    <div class="card-content">
      <!-- Meta -->
      <div class="news-meta">
        <time class="news-date" :datetime="newsItem.createdAt.toISOString()">
          <i class="fas fa-calendar-alt"></i>
          {{ formatDate(newsItem.createdAt) }}
        </time>
        <span v-if="readingTime" class="reading-time">
          <i class="fas fa-clock"></i>
          {{ readingTime }} {{ $t('news.readingTimeFull') }}
        </span>
      </div>

      <!-- Categories -->
      <div v-if="newsItem.categories && newsItem.categories.length" class="categories">
        <span v-for="category in newsItem.categories" :key="category" class="category-tag">
          {{ category }}
        </span>
      </div>

      <!-- Title -->
      <h3 class="news-title">{{ newsItem.title }}</h3>

      <!-- Excerpt -->
      <p class="news-excerpt">{{ excerpt }}</p>

      <!-- Footer -->
      <div class="card-footer">
        <span class="news-author">
          <i class="fas fa-user"></i>
          {{ authorName }}
        </span>
        <button class="read-more-button" @click.stop="$emit('click', newsItem)">
          {{ $t('news.readMore') }}
          <i class="fas fa-arrow-right"></i>
        </button>
      </div>
    </div>
  </article>
</template>

<script setup lang="ts">
import { computed, getCurrentInstance } from 'vue';
import type { NewsItem } from '@/composables/useNews';
import type { SchoolNewsItem } from '@/composables/useSchoolNews';
import { publicAuthorName } from '@/utils/author';
import { extractPlainText, sanitizeRichHtml } from '@/utils/sanitizeHtml';

const instance = getCurrentInstance();
const $t = instance?.appContext.config.globalProperties.$t;

const props = defineProps<{
  newsItem: NewsItem | SchoolNewsItem;
  loading?: boolean;
}>();

defineEmits<{
  click: [item: NewsItem | SchoolNewsItem];
}>();

const sanitizedContent = computed(() => sanitizeRichHtml(props.newsItem.content));

const featuredImage = computed(() => {
  const imgMatch = sanitizedContent.value.match(/<img[^>]+src="([^">]+)"/i);
  return imgMatch ? imgMatch[1] : null;
});

const excerpt = computed(() => {
  const text = extractPlainText(sanitizedContent.value);
  const maxLength = 150;
  return text.length > maxLength ? text.substring(0, maxLength).trim() + '...' : text;
});

const readingTime = computed(() => {
  const text = extractPlainText(sanitizedContent.value);
  const words = text.split(/\s+/).length;
  return Math.ceil(words / 200); // 200 palabras por minuto
});

const authorName = computed(() => publicAuthorName(props.newsItem.author));

const formatDate = (date: Date): string => {
  return new Intl.DateTimeFormat('es-ES', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  }).format(date);
};
</script>

<style scoped>
.news-card {
  background: white;
  border-radius: var(--border-radius-xl);
  box-shadow: var(--shadow-md);
  overflow: hidden;
  transition: var(--transition-all);
  cursor: pointer;
  display: flex;
  flex-direction: column;
  height: 100%;
  position: relative;
  animation: fadeInUp 0.6s ease-out;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.news-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

.news-card.featured {
  border: 2px solid var(--color-secondary-400);
  box-shadow: 0 8px 30px rgba(234, 179, 8, 0.2);
}

.news-card.featured:hover {
  box-shadow: 0 12px 40px rgba(234, 179, 8, 0.3);
}

.featured-badge {
  position: absolute;
  top: var(--spacing-3);
  right: var(--spacing-3);
  background: linear-gradient(135deg, var(--color-secondary-500), var(--color-secondary-600));
  color: white;
  padding: var(--spacing-2) var(--spacing-3);
  border-radius: var(--border-radius-full);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-bold);
  display: flex;
  align-items: center;
  gap: var(--spacing-1);
  z-index: 2;
  box-shadow: var(--shadow-lg);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.image-wrapper {
  position: relative;
  width: 100%;
  height: 240px;
  overflow: hidden;
  background: linear-gradient(135deg, var(--color-neutral-100), var(--color-neutral-200));
}

.featured-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform 0.4s ease;
}

.news-card:hover .featured-image {
  transform: scale(1.08);
}

.image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: var(--color-neutral-400);
  background: linear-gradient(135deg, var(--color-neutral-100), var(--color-neutral-200));
}

.card-content {
  padding: var(--spacing-5);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-3);
  flex: 1;
}

.news-meta {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-500);
}

.news-date,
.reading-time {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
}

.reading-time {
  color: var(--color-primary-600);
  font-weight: var(--font-weight-medium);
}

.categories {
  display: flex;
  gap: var(--spacing-2);
  flex-wrap: wrap;
}

.category-tag {
  background: var(--color-primary-100);
  color: var(--color-primary-700);
  padding: var(--spacing-1) var(--spacing-2);
  border-radius: var(--border-radius-md);
  font-size: var(--font-size-xs);
  font-weight: var(--font-weight-medium);
}

.news-title {
  font-size: var(--font-size-xl);
  font-weight: var(--font-weight-bold);
  color: var(--color-neutral-900);
  margin: 0;
  line-height: 1.3;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.news-excerpt {
  color: var(--color-neutral-600);
  line-height: 1.6;
  margin: 0;
  display: -webkit-box;
  -webkit-line-clamp: 3;
  -webkit-box-orient: vertical;
  overflow: hidden;
  flex: 1;
}

.card-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--spacing-3);
  margin-top: var(--spacing-2);
  padding-top: var(--spacing-4);
  border-top: 1px solid var(--color-neutral-200);
}

.news-author {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  color: var(--color-neutral-600);
}

.read-more-button {
  display: flex;
  align-items: center;
  gap: var(--spacing-2);
  background: var(--color-primary-600);
  color: white;
  border: none;
  padding: var(--spacing-2) var(--spacing-4);
  border-radius: var(--border-radius-lg);
  font-weight: var(--font-weight-medium);
  font-size: var(--font-size-sm);
  cursor: pointer;
  transition: var(--transition-all);
  white-space: nowrap;
}

.read-more-button:hover {
  background: var(--color-primary-700);
  transform: translateX(4px);
}

.read-more-button i {
  font-size: var(--font-size-xs);
  transition: transform 0.3s ease;
}

.read-more-button:hover i {
  transform: translateX(4px);
}

/* Loading State */
.news-card.loading-state {
  pointer-events: none;
}

.news-card.loading-state .image-wrapper,
.news-card.loading-state .news-title,
.news-card.loading-state .news-excerpt {
  background: linear-gradient(90deg, var(--color-neutral-200) 25%, var(--color-neutral-100) 50%, var(--color-neutral-200) 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

@keyframes shimmer {
  0% {
    background-position: 200% 0;
  }
  100% {
    background-position: -200% 0;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .image-wrapper {
    height: 200px;
  }

  .card-content {
    padding: var(--spacing-4);
  }

  .news-title {
    font-size: var(--font-size-lg);
  }

  .card-footer {
    flex-direction: column;
    align-items: flex-start;
  }

  .read-more-button {
    width: 100%;
    justify-content: center;
  }
}
</style>
